import type DevExpress from 'devextreme/bundles/dx.all';
import CustomStore from 'devextreme/data/custom_store';
import * as ko from 'knockout';
import { DxWidget } from '../../../AbstractWidget';
import { AS, datagrid, datagridButton, refreshDx } from '../../../dx_helper';
import * as TRANSLATIONS from '../../../enums/translations';
import { assertNever, DONE } from '../../../helper';
import * as API from '../../../its-itembank-api.g';
import { legacyPushPull } from '../../docmanager';
import { ServerConnection } from '../../RestAPI';
import * as ROUTES from '../../routes';
import { registerOnUpdateToolbar } from '../../toptoolbar.service';
import { superuser } from '../../ui-routes';
import { UIAction } from '../../uiAction';
import * as i18next from './../../../i18n/i18n';
import { htmlString } from './examorderoverview.html.g';
import { IParams, WIDGET_NAME } from './route';

type LIST = API.IUi_Superuser_Examorderoverview_DataQuery['examOrder']['list'][0];

function convertStatus(x: API.ExaminationOrderStatus): TRANSLATIONS.StatusString {
    switch (x) {
        case API.ExaminationOrderStatus.Open:
            return 'open';
        case API.ExaminationOrderStatus.Populating:
            return 'populating';
        case API.ExaminationOrderStatus.Published:
            return 'published';
        case API.ExaminationOrderStatus.Ready:
            return 'ready';
        default:
            assertNever(x);
            throw new Error();
    }

}

export class ViewModel extends DxWidget {


    public readonly legendHeader = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.POSSIBLE_STATUS_DISPLAYS']);
    });
    public readonly legendOpenText = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.OPEN']);
    });
    public readonly legendOpenHint = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.THE_EXAM_ORDER_HAS_NOT_YET_BEEN_SENT_TO_THE_SUBJECT_COORDINATOR_AND_CAN_BE_EDITED']);
    });
    public readonly legendPopulatingText = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.POPULATING']);
    });
    public readonly legendPopulatingHint = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.THE_EXAM_ORDER_HAS_BEEN_SENT_TO_THE_SUBJECT_COORDINATOR_WHO_CHOOSES_ITEMS_FOR_THIS_EXAM']);
    });
    public readonly legendReadyText = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.READY']);
    });
    public readonly legendReadyHint = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.THE_SUBJECT_COORDINATOR_HAS_SELECTED_ALL_ITEMS_FOR_THIS_EXAM_ORDER_AND_RELEASED_IT_THIS_EXAM_ORDER_CAN_NOW_BE_MADE_INTO_AN_EXAM']);
    });
    public readonly legendFinishedText = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.FINISHED']);
    });
    public readonly legendFinishedHint = ko.pureComputed(() => {
        return i18next.t(['ui.superuser.examorderoverview.AN_EXAM_HAS_BEEN_CREATED_FROM_THE_EXAM_ORDER']);
    });

    public readonly gridOptions = ko.pureComputed(() => {
        const retVal = datagrid({
            WIDGET_NAME,
            widget: this,
            config: {
                columns: [],
                allowColumnResizing: true,
                allowColumnReordering: true,
                columnChooser: {
                    enabled: true,
                    mode: 'select'
                },
                sorting: {
                    mode: 'single',
                },

                searchPanel: {
                    visible: true
                },
                filterPanel: {
                    visible: true
                },
                headerFilter: {
                    visible: true,
                },
                selection: {
                    mode: 'single'
                },
                groupPanel: {
                    visible: false
                },
                dataSource: {
                    store: new CustomStore({
                        loadMode: 'raw',
                        key: 'docReferenceId',
                        load: async () => {
                            return this._examorder;
                        },
                        byKey: async key => {
                            return this._examorder.find(x => x.docReferenceId === key);
                        }
                    })
                }
            }
        });

        retVal.columns.push({
            caption: i18next.t(['ui.superuser.examorderoverview.NAME_OF_EXAM_ORDER']),
            dataField: 'title',
            sortOrder: 'asc',
            sortIndex: 0,
            dataType: 'string',
        });
        retVal.columns.push({
            caption: i18next.t(['ui.superuser.examorderoverview.ID']),
            dataField: 'examinationOrderId',
            dataType: 'string',
        });
        retVal.columns.push({
            caption: i18next.t(['ui.superuser.examorderoverview.STATUS']),
            dataType: 'string',
            dataField: 'status',
            calculateCellValue: data => {
                const x: LIST = data;
                return i18next.t(TRANSLATIONS.StatusString[convertStatus(x.status)]);
            }
        });
        retVal.columns.push({
            visible: false,
            caption: i18next.t(['ui.superuser.examorderoverview.TYPE']),
            dataType: 'string',
            dataField: 'type',
            calculateCellValue: data => {
                const x: LIST = data;
                return i18next.t(TRANSLATIONS.LevelOfSecrecyString[x.levelOfSecrecy]);
            }
        });
        retVal.columns.push({
            caption: i18next.t(['ui.superuser.examorderoverview.PROFILE']),
            dataType: 'string',
            dataField: 'profile.title',
        });
        retVal.columns.push({
            caption: i18next.t(['ui.superuser.examorderoverview.POINTS']),
            width: 80,
            dataType: 'number',
            dataField: 'scoreMax',
        });
        retVal.columns.push({
            visible: false,
            caption: i18next.t(['ui.superuser.examorderoverview.DATE_OF_EXAM']),
            dataType: 'string',
            dataField: 'examDate',
        });
        retVal.columns.push({
            visible: false,
            caption: i18next.t(['ui.superuser.examorderoverview.START_TIME']),
            dataType: 'string',
            dataField: 'examTime',
        });
        retVal.columns.push({
            caption: i18next.t(['ui.superuser.examorderoverview.LANGUAGES']),
            dataType: 'string',
            calculateDisplayValue: x => {
                const queryRow: LIST = x;
                return queryRow.localizations.map(x => x.name.value).sort().join(', ');
            }
        });

        retVal.columns.push({
            caption: i18next.t(['ui.superuser.examorderoverview.CREATED_AT']),
            dataType: 'datetime',
            dataField: 'dateCreated',
            cssClass: 'itsr3-uitest-hide',
        });

        retVal.columns.push({
            caption: i18next.t(['ui.superuser.examorderoverview.DEACTIVATED']),
            width: 80,
            dataField: 'isDeleted',
            dataType: 'boolean',
        });

        retVal.columns.push({
            type: 'buttons',
            caption: i18next.t(['ui.superuser.examorderoverview.ACTIONS']),
            buttons: [
                datagridButton({
                    text: i18next.t(['ui.superuser.examorderoverview.EDIT']),
                    action: this.actionEdit,
                }),
                datagridButton({
                    text: i18next.t(['ui.superuser.examorderoverview.COPY']),
                    action: this.actionCopy,
                })
            ]
        });
        retVal.filterValue = ['isDeleted', '=', false];
        return retVal;
    });

    public readonly actionCopy = new UIAction<LIST>(undefined, async (e, args) => {
        const newExamOrder = await ServerConnection.api.ui_superuser_examorderoverview_copy({
            examOrderDocRef: args.docReferenceId
        });
        await ROUTES.routeManager.navigateToHREF(superuser.ExamOrderEdit.href({
            examOrderDocRefId: newExamOrder.examOrder.copy.docReferenceId
        }));
    });
    public readonly actionEdit = new UIAction<LIST>(undefined, async (e, args) => {
        await ROUTES.routeManager.navigateToHREF(superuser.ExamOrderEdit.href({
            examOrderDocRefId: args.docReferenceId
        }));

    });

    public readonly _examorder: LIST[] = [];

    public async OnRefresh() {
        await super.OnRefresh();
        const result = await ServerConnection.api.ui_superuser_examorderoverview_data({
        });
        const d = result?.examOrder?.list ?? [];
        this._examorder.splice(0, this._examorder.length, ...d);

        await refreshDx(this);
    }


    constructor(readonly params: IParams) {
        super();
    }
    public readonly loaded = ko.observable(false);
    public async initialize() {
        await super.initialize();
        await this.OnRefresh();
        this.disposables.addDiposable(registerOnUpdateToolbar(x => this.onPrepareTopToolbar(x)));
        this.loaded(true);
    }

    public readonly actionNewExamOrder = new UIAction(undefined, async () => {
        const result = await legacyPushPull(() => ServerConnection.api.ui_superuser_examorderoverview_new({}));
        const d = result;

        const examOrderDocRefId = d?.examOrder?.new?.docReferenceId;
        if (!examOrderDocRefId) {
            throw new Error('unable to retrieve id');
        }

        await ROUTES.routeManager.navigateToHREF(superuser.ExamOrderEdit.href({
            examOrderDocRefId
        }));

    });

    private onPrepareTopToolbar(toolbar: DevExpress.ui.dxToolbar.Properties) {
        toolbar.items.push(AS<DevExpress.ui.dxToolbar.Item>({
            location: 'after',
            widget: 'dxButton',
            options: AS<DevExpress.ui.dxButton.Properties>({
                icon: 'plus',
                text: i18next.t(['ui.superuser.examorderoverview.NEW']),
                onClick: this.actionNewExamOrder.click
            })
        }));
        return DONE;
    }

}

export function create(params: IParams, componentInfo: ko.components.ComponentInfo) {
    const retVal = new ViewModel(params);
    retVal.DoInit({ WIDGET_NAME });
    return retVal;
}

ko.components.register(WIDGET_NAME, {
    viewModel: {
        createViewModel: create
    },
    template: htmlString.replace(/@@@/g, WIDGET_NAME)
});
